// src/components/views/FinanceiroView.jsx
import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import Pagination from '../Pagination'; 
import { formatCurrency } from '../../utils/formatters';

const FinanceiroView = ({ financialFlow, openModal, sales, scrollTo, onScrollComplete }) => {
    const tableRef = useRef(null);
    const [expandedRowKey, setExpandedRowKey] = useState(null);

    const [receivablesFilters, setReceivablesFilters] = useState({ search: '', year: 'all', month: 'all' });
    const [receivablesPage, setReceivablesPage] = useState(1);
    const [receivablesItemsPerPage, setReceivablesItemsPerPage] = useState(5);
    const [receivablesSort, setReceivablesSort] = useState({ key: 'dueDate', direction: 'asc' });

    const [historyFilters, setHistoryFilters] = useState({ search: '', year: 'all', month: 'all' });
    const [historyPage, setHistoryPage] = useState(1);
    const [historyItemsPerPage, setHistoryItemsPerPage] = useState(5);
    const [historySort, setHistorySort] = useState({ key: 'paymentDate', direction: 'desc' });
    
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
    const [selectedMetric, setSelectedMetric] = useState('monthlyToReceive');

    useEffect(() => {
        if (scrollTo === 'contas-a-receber') {
            tableRef.current?.scrollIntoView({ behavior: 'smooth' });
            onScrollComplete(); 
        }
    }, [scrollTo, onScrollComplete]);

    const months = [
        "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
        "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
    ];

    const handleReceivablesSort = useCallback((key) => {
        setReceivablesSort(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
    }, []);

    const handleHistorySort = useCallback((key) => {
        setHistorySort(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
    }, []);
    
    const handleReceivablesFilterChange = (e) => {
        const { name, value } = e.target;
        setReceivablesFilters(prev => {
            const newFilters = { ...prev, [name]: value };
            if (name === 'year') {
                newFilters.month = 'all';
            }
            return newFilters;
        });
    };

    const handleHistoryFilterChange = (e) => {
        const { name, value } = e.target;
        setHistoryFilters(prev => {
            const newFilters = { ...prev, [name]: value };
            if (name === 'year') {
                newFilters.month = 'all';
            }
            return newFilters;
        });
    };

    const sortItems = (items, sortConfig) => {
        const sortedItems = [...items]; 
        sortedItems.sort((a, b) => {
            const valA = a[sortConfig.key] || '';
            const valB = b[sortConfig.key] || '';
            if (valA instanceof Date && valB instanceof Date) {
                return sortConfig.direction === 'asc' ? valA.getTime() - valB.getTime() : valB.getTime() - valA.getTime();
            }
            if (typeof valA === 'string') {
                return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
            }
            if (typeof valA === 'number') {
                return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
            }
            return 0;
        });
        return sortedItems;
    };
    
    const filteredAndSortedReceivables = useMemo(() => {
        let items = financialFlow.receivables || [];
        if (receivablesFilters.search) {
            const searchLower = receivablesFilters.search.toLowerCase();
            items = items.filter(r => 
                r.customerName?.toLowerCase().includes(searchLower) ||
                r.productName?.toLowerCase().includes(searchLower)
            );
        }
        if (receivablesFilters.year !== 'all') {
            items = items.filter(r => r.dueDate && r.dueDate.getFullYear().toString() === receivablesFilters.year.toString());
        }
        if (receivablesFilters.month !== 'all') {
            items = items.filter(r => r.dueDate && r.dueDate.getMonth().toString() === receivablesFilters.month.toString());
        }
        return sortItems(items, receivablesSort);
    }, [financialFlow.receivables, receivablesFilters, receivablesSort]);

    const paginatedReceivables = useMemo(() => {
        const startIndex = (receivablesPage - 1) * receivablesItemsPerPage;
        return filteredAndSortedReceivables.slice(startIndex, startIndex + receivablesItemsPerPage);
    }, [filteredAndSortedReceivables, receivablesPage, receivablesItemsPerPage]);

    const filteredAndSortedHistory = useMemo(() => {
        let items = financialFlow.receivedHistory || [];
        if (historyFilters.search) {
            const searchLower = historyFilters.search.toLowerCase();
            items = items.filter(r => 
                r.customerName?.toLowerCase().includes(searchLower) ||
                r.productName?.toLowerCase().includes(searchLower)
            );
        }
        if (historyFilters.year !== 'all') {
            items = items.filter(r => r.paymentDate && r.paymentDate.getFullYear().toString() === historyFilters.year.toString());
        }
        if (historyFilters.month !== 'all') {
            items = items.filter(r => r.paymentDate && r.paymentDate.getMonth().toString() === historyFilters.month.toString());
        }
        return sortItems(items, historySort);
    }, [financialFlow.receivedHistory, historyFilters, historySort]);

    const paginatedHistory = useMemo(() => {
        const startIndex = (historyPage - 1) * historyItemsPerPage;
        return filteredAndSortedHistory.slice(startIndex, startIndex + historyItemsPerPage);
    }, [filteredAndSortedHistory, historyPage, historyItemsPerPage]);
    
    const monthlyChartData = useMemo(() => {
        const yearData = financialFlow.byYear[selectedYear];
        if (!yearData) {
            return {
                labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
                datasets: []
            };
        }
        const data = selectedMetric === 'monthlyToReceive' ? yearData.monthlyToReceive : yearData.monthlyReceived;
        const label = selectedMetric === 'monthlyToReceive' ? 'Previsão de Recebimentos' : 'Pagamentos Recebidos';
        const backgroundColor = selectedMetric === 'monthlyToReceive' ? 'rgba(215, 191, 189, 0.6)' : 'rgba(163, 201, 163, 0.6)';
        const borderColor = selectedMetric === 'monthlyToReceive' ? 'rgba(215, 191, 189, 1)' : 'rgba(163, 201, 163, 1)';
        return {
            labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
            datasets: [{
                label: `${label} em ${selectedYear}`,
                data: data,
                backgroundColor: backgroundColor,
                borderColor: borderColor,
                borderWidth: 1,
            }]
        };
    }, [selectedYear, selectedMetric, financialFlow.byYear]);
    
    const handleToggleExpand = (key) => {
        setExpandedRowKey(prevKey => (prevKey === key ? null : key));
    };

    return (
        <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-800">Fluxo de Caixa</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl border border-[#F2DFDB]">
                    <h3 className="text-sm font-medium text-gray-500">A Receber (Este Mês)</h3>
                    <p className="text-3xl font-bold text-gray-800 mt-2">{formatCurrency(financialFlow.summary.toReceiveThisMonth)}</p>
                </div>
                <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                    <h3 className="text-sm font-medium text-green-700">Recebido (Este Mês)</h3>
                    <p className="text-3xl font-bold text-green-900 mt-2">{formatCurrency(financialFlow.summary.receivedThisMonth)}</p>
                </div>
            </div>
            
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#F2DFDB]">
                 <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                    <div className="flex items-center gap-2">
                         <button onClick={() => setSelectedMetric('monthlyToReceive')} className={`px-3 py-1 text-sm rounded-lg ${selectedMetric === 'monthlyToReceive' ? 'custom-button' : 'bg-gray-200'}`}>A Receber</button>
                         <button onClick={() => setSelectedMetric('monthlyReceived')} className={`px-3 py-1 text-sm rounded-lg ${selectedMetric === 'monthlyReceived' ? 'custom-button' : 'bg-gray-200'}`}>Recebido</button>
                    </div>
                    <div className="flex items-center">
                        <label htmlFor="year-select-finance" className="mr-2 text-sm font-medium">Ano:</label>
                        <select 
                            id="year-select-finance" 
                            value={selectedYear} 
                            onChange={(e) => setSelectedYear(e.target.value)} 
                            className="px-4 py-2 bg-white border border-[#D7BFBD] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D7BFBD]"
                        >
                            {financialFlow.availableYears.map(year => <option key={year} value={year}>{year}</option>)}
                        </select>
                    </div>
                </div>
                 <div className="h-72">
                    <Bar data={monthlyChartData} options={{ responsive: true, maintainAspectRatio: false, scales: { y: { ticks: { callback: value => formatCurrency(value) } } } }}/>
                 </div>
            </div>

            <div ref={tableRef} id="contas-a-receber" className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-[#F2DFDB]">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Contas a Receber</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    <input 
                        type="text" 
                        name="search"
                        value={receivablesFilters.search} 
                        onChange={handleReceivablesFilterChange}
                        placeholder="🔎 Pesquisar..." 
                        className="w-full lg:col-span-1 px-4 py-2 bg-white border border-[#D7BFBD] rounded-lg"
                    />
                    <select name="year" value={receivablesFilters.year} onChange={handleReceivablesFilterChange} className="w-full lg:col-span-1 px-4 py-2 bg-white border border-[#D7BFBD] rounded-lg">
                        <option value="all">Todos os Anos</option>
                        {financialFlow.availableYears.map(year => <option key={year} value={year}>{year}</option>)}
                    </select>
                    <select name="month" value={receivablesFilters.month} onChange={handleReceivablesFilterChange} className="w-full lg:col-span-1 px-4 py-2 bg-white border border-[#D7BFBD] rounded-lg" disabled={receivablesFilters.year === 'all'}>
                        <option value="all">Todos os Meses</option>
                        {months.map((month, index) => <option key={index} value={index}>{month}</option>)}
                    </select>
                </div>

                <div className="space-y-4 md:hidden">
                    {paginatedReceivables.length > 0 ? paginatedReceivables.map(item => {
                        const sale = sales.find(s => s.id === item.saleId);
                        const isExpanded = expandedRowKey === `receivable-${item.saleId}-${item.installmentNumber}`;
                        return (
                            <div key={`receivable-${item.saleId}-${item.installmentNumber}`} className="border rounded-lg p-4 space-y-3">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h3 className="font-bold text-gray-800">{item.productName}{sale && sale.items.length > 1 ? ` (+${sale.items.length - 1})` : ''}</h3>
                                        <p className="text-sm text-gray-600">{item.customerName || 'N/A'}</p>
                                    </div>
                                    <p className="font-bold text-lg text-red-600 flex-shrink-0 ml-2">{formatCurrency(item.amountToReceive)}</p>
                                </div>
                                <div className="flex justify-between items-center pt-3 border-t">
                                    <p className="text-sm font-medium text-gray-500">Vence: {item.dueDate ? new Date(item.dueDate).toLocaleDateString('pt-BR') : 'N/A'}</p>
                                    <div className="flex items-center gap-2">
                                        {sale && sale.items.length > 1 && (<button className="action-btn text-xl" onClick={() => handleToggleExpand(`receivable-${item.saleId}-${item.installmentNumber}`)}>{isExpanded ? '▲' : '▼'}</button>)}
                                        <button className="action-btn text-xl" onClick={() => openModal('addPayment', sale)} title="Adicionar Pagamento">💰</button>
                                    </div>
                                </div>
                                {isExpanded && (
                                    <div className="pt-2 mt-2 border-t text-sm space-y-1">
                                        <h4 className="font-semibold text-gray-600">Itens:</h4>
                                        {sale?.items.map((saleItem, index) => (
                                            <p key={index} className="flex justify-between text-gray-700">
                                                <span>{saleItem.quantity}x {saleItem.productName}</span>
                                                <span>{formatCurrency(saleItem.unitPrice * saleItem.quantity)}</span>
                                            </p>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    }) : (
                        <p className="p-4 text-center text-gray-500">Nenhuma conta a receber para os filtros selecionados.</p>
                    )}
                </div>

                <div className="overflow-x-auto hidden md:block">
                    <table className="w-full text-left">
                        <thead className="border-b-2 border-[#F2DFDB]">
                            <tr>
                                <th className="p-4 w-8"></th>
                                <th className="p-4 text-sm font-semibold sortable-header" onClick={() => handleReceivablesSort('dueDate')}>Vencimento</th>
                                <th className="p-4 text-sm font-semibold sortable-header" onClick={() => handleReceivablesSort('customerName')}>Cliente</th>
                                <th className="p-4 text-sm font-semibold sortable-header" onClick={() => handleReceivablesSort('productName')}>Venda</th>
                                <th className="p-4 text-sm font-semibold sortable-header" onClick={() => handleReceivablesSort('amountToReceive')}>Valor a Receber</th>
                                <th className="p-4 text-sm font-semibold text-center">Ação</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedReceivables.length > 0 ? paginatedReceivables.map(item => {
                                const sale = sales.find(s => s.id === item.saleId);
                                const isExpanded = expandedRowKey === `receivable-${item.saleId}-${item.installmentNumber}`;
                                return (
                                    <React.Fragment key={`receivable-${item.saleId}-${item.installmentNumber}`}>
                                        <tr className="border-b border-[#F2DFDB] hover:bg-[#F4EFEC]">
                                            <td className="p-4 text-center">
                                                {sale && sale.items.length > 1 && (
                                                    <button onClick={() => handleToggleExpand(`receivable-${item.saleId}-${item.installmentNumber}`)} className="text-lg">
                                                        {isExpanded ? '▲' : '▼'}
                                                    </button>
                                                )}
                                            </td>
                                            <td className="p-4 text-gray-600">{item.dueDate ? new Date(item.dueDate).toLocaleDateString('pt-BR') : 'N/A'}</td>
                                            <td className="p-4 font-medium text-gray-800">{item.customerName || 'N/A'}</td>
                                            <td className="p-4 text-gray-600">{item.productName}</td>
                                            <td className="p-4 font-bold text-red-600">{formatCurrency(item.amountToReceive)}</td>
                                            <td className="p-4 text-center">
                                                <button className="action-btn" onClick={() => openModal('addPayment', sale)} title="Adicionar Pagamento">
                                                    💰
                                                </button>
                                            </td>
                                        </tr>
                                        {isExpanded && (
                                            <tr className="bg-gray-50">
                                                <td colSpan="6" className="p-4">
                                                    <div className="space-y-2">
                                                        <h4 className="font-semibold">Itens da Venda:</h4>
                                                        {sale?.items.map((saleItem, index) => (
                                                            <div key={index} className="flex justify-between text-sm ml-4">
                                                                <span>{saleItem.quantity}x {saleItem.productName}</span>
                                                                <span>{formatCurrency(saleItem.unitPrice * saleItem.quantity)}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                )
                            }) : (
                                <tr>
                                    <td colSpan="6" className="p-4 text-center text-gray-500">Nenhuma conta a receber para os filtros selecionados.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    totalItems={filteredAndSortedReceivables.length}
                    itemsPerPage={receivablesItemsPerPage}
                    currentPage={receivablesPage}
                    onPageChange={setReceivablesPage}
                    onItemsPerPageChange={(val) => { setReceivablesItemsPerPage(Number(val)); setReceivablesPage(1); }}
                />
            </div>
            
            <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-[#F2DFDB]">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Histórico de Recebimentos</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    <input 
                        type="text" 
                        name="search"
                        value={historyFilters.search} 
                        onChange={handleHistoryFilterChange}
                        placeholder="🔎 Pesquisar..." 
                        className="w-full lg:col-span-1 px-4 py-2 bg-white border border-[#D7BFBD] rounded-lg"
                    />
                     <select name="year" value={historyFilters.year} onChange={handleHistoryFilterChange} className="w-full lg:col-span-1 px-4 py-2 bg-white border border-[#D7BFBD] rounded-lg">
                        <option value="all">Todos os Anos</option>
                        {financialFlow.availableYears.map(year => <option key={year} value={year}>{year}</option>)}
                    </select>
                    <select name="month" value={historyFilters.month} onChange={handleHistoryFilterChange} className="w-full lg:col-span-1 px-4 py-2 bg-white border border-[#D7BFBD] rounded-lg" disabled={historyFilters.year === 'all'}>
                        <option value="all">Todos os Meses</option>
                        {months.map((month, index) => <option key={index} value={index}>{month}</option>)}
                    </select>
                </div>
                
                <div className="space-y-4 md:hidden">
                    {paginatedHistory.length > 0 ? paginatedHistory.map((item, index) => {
                        const sale = sales.find(s => s.id === item.saleId);
                        const uniqueKey = `${item.saleId}-${item.paymentDate.toISOString()}-${index}`;
                        const isExpanded = expandedRowKey === uniqueKey;
                        return (
                            <div key={uniqueKey} className="border rounded-lg p-4 space-y-3">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h3 className="font-bold text-gray-800">{item.productName}{sale && sale.items.length > 1 ? ` (+${sale.items.length - 1})` : ''}</h3>
                                        <p className="text-sm text-gray-600">{item.customerName || 'N/A'}</p>
                                    </div>
                                    <p className="font-bold text-lg text-green-600 flex-shrink-0 ml-2">{formatCurrency(item.amount)}</p>
                                </div>
                                <div className="flex justify-between items-center pt-3 border-t">
                                    <p className="text-sm font-medium text-gray-500">Pago em: {item.paymentDate ? new Date(item.paymentDate).toLocaleDateString('pt-BR') : 'N/A'}</p>
                                    {sale && sale.items.length > 1 && (<button className="action-btn text-xl" onClick={() => handleToggleExpand(uniqueKey)}>{isExpanded ? '▲' : '▼'}</button>)}
                                </div>
                                {isExpanded && (
                                    <div className="pt-2 mt-2 border-t text-sm space-y-1">
                                        <h4 className="font-semibold text-gray-600">Itens:</h4>
                                        {sale?.items.map((saleItem, itemIndex) => (
                                            <p key={itemIndex} className="flex justify-between text-gray-700">
                                                <span>{saleItem.quantity}x {saleItem.productName}</span>
                                                <span>{formatCurrency(saleItem.unitPrice * saleItem.quantity)}</span>
                                            </p>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    }) : (
                        <p className="p-4 text-center text-gray-500">Nenhum pagamento registrado para os filtros selecionados.</p>
                    )}
                </div>

                <div className="overflow-x-auto hidden md:block">
                    <table className="w-full text-left">
                        <thead className="border-b-2 border-[#F2DFDB]">
                            <tr>
                                <th className="p-4 w-8"></th>
                                <th className="p-4 text-sm font-semibold sortable-header" onClick={() => handleHistorySort('paymentDate')}>Data</th>
                                <th className="p-4 text-sm font-semibold sortable-header" onClick={() => handleHistorySort('customerName')}>Cliente</th>
                                <th className="p-4 text-sm font-semibold sortable-header" onClick={() => handleHistorySort('productName')}>Venda</th>
                                <th className="p-4 text-sm font-semibold sortable-header" onClick={() => handleHistorySort('amount')}>Valor Pago</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedHistory.length > 0 ? paginatedHistory.map((item, index) => {
                                const sale = sales.find(s => s.id === item.saleId);
                                const uniqueKey = `${item.saleId}-${item.paymentDate.toISOString()}-${index}`;
                                const isExpanded = expandedRowKey === uniqueKey;
                                return (
                                    <React.Fragment key={uniqueKey}>
                                        <tr className="border-b border-[#F2DFDB] hover:bg-[#F4EFEC]">
                                            <td className="p-4 text-center">
                                                {sale && sale.items.length > 1 && (
                                                    <button onClick={() => handleToggleExpand(uniqueKey)} className="text-lg">
                                                        {isExpanded ? '▲' : '▼'}
                                                    </button>
                                                )}
                                            </td>
                                            <td className="p-4 text-gray-600">{item.paymentDate ? new Date(item.paymentDate).toLocaleDateString('pt-BR') : 'N/A'}</td>
                                            <td className="p-4 font-medium text-gray-800">{item.customerName || 'N/A'}</td>
                                            <td className="p-4 text-gray-600">{item.productName}</td>
                                            <td className="p-4 font-bold text-green-600">{formatCurrency(item.amount)}</td>
                                        </tr>
                                        {isExpanded && (
                                            <tr className="bg-gray-50">
                                                <td colSpan="5" className="p-4">
                                                    <div className="space-y-2">
                                                        <h4 className="font-semibold">Itens da Venda Registrada:</h4>
                                                        {sale?.items.map((saleItem, itemIndex) => (
                                                            <div key={itemIndex} className="flex justify-between text-sm ml-4">
                                                                <span>{saleItem.quantity}x {saleItem.productName}</span>
                                                                <span>{formatCurrency(saleItem.unitPrice * saleItem.quantity)}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                )
                            }) : (
                                <tr>
                                    <td colSpan="5" className="p-4 text-center text-gray-500">Nenhum pagamento registrado para os filtros selecionados.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    totalItems={filteredAndSortedHistory.length}
                    itemsPerPage={historyItemsPerPage}
                    currentPage={historyPage}
                    onPageChange={setHistoryPage}
                    onItemsPerPageChange={(val) => { setHistoryItemsPerPage(Number(val)); setHistoryPage(1); }}
                />
            </div>
        </div>
    );
};

export default FinanceiroView;